import { Autocomplete, Dialog, DialogContent, FormControlLabel, DialogTitle, TextField, Typography, Grid, DialogActions, Button, Checkbox, Box, Chip } from "@mui/material";
import { getAllInsumos } from "components-base/insumos/functions";
import React, { useState, useEffect } from "react";
import { createPrendaInsumo, modifyPrendaInsumo } from "../functions";
import { useSelector } from "react-redux";
import { getAllTallasByProductoId } from "components-base/productos/functions";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import StraightenIcon from "@mui/icons-material/Straighten";

const UNIDADES = [
  { name: "Centimetros", factor: 100 },
  { name: "Metros", factor: 1 },
  { name: "Milimetros", factor: 1000 },
  { name: "Pulgadas", factor: 2.54 },
];
const DEFAULT_UNIDAD = UNIDADES[0];

const inputSx = {
  "& .MuiOutlinedInput-root": {
    fontFamily: "'Poppins', sans-serif",
    fontSize: "0.85rem",
    borderRadius: 2,
    "&.Mui-focused fieldset": { borderColor: "#f43f5e" },
    "& input": { color: "inherit" },
  },
  "& label": { fontFamily: "'Poppins', sans-serif", fontSize: "0.85rem" },
  "& label.Mui-focused": { color: "#f43f5e" },
  // dark mode
  ".dark & .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.2)" },
  ".dark & .MuiInputLabel-root": { color: "rgba(255,255,255,0.6)" },
  ".dark & .MuiInputBase-input": { color: "#f3f4f6" },
  ".dark & .MuiSvgIcon-root": { color: "rgba(255,255,255,0.5)" },
};

const sectionLabelSx = {
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 600,
  fontSize: "0.72rem",
  color: "#be185d",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  mb: 1.5,
};

const InsumosModal = ({ tallas, setTallas, editMode, editData, mode, open, handleClose, setDataPI, setPrendaInsumo }) => {
  const [insumos, setInsumos] = useState(null);
  const [cantidad, setCantidad] = useState(0);
  const [unidad, setUnidad] = useState(null);
  const [insumoSelected, setInsumoSelected] = useState(null);
  const [checkTallas, setCheckTallas] = useState(false);
  const { defaultData: prendaSelected } = useSelector(({ prenda }) => prenda);
  const { productIdSelected } = useSelector(({ producto }) => producto);

  useEffect(() => {
    const fetchData = async () => {
      const data = await getAllInsumos();
      setInsumos(data);
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (editMode !== "edit" || (editData && !editData.details && editData.unidad === "Rollo")) {
      const fetchData = async () => {
        const productoId = prendaSelected?.productoId || productIdSelected;
        if (!productoId) return;
        const tallasData = await getAllTallasByProductoId(productoId);
        setTallas(tallasData.map(item => ({
          talla: item.talla,
          cantidad: "",
          unidad: DEFAULT_UNIDAD,
          insumo: "",
        })));
      };
      fetchData();
    }
  }, [prendaSelected, editMode, productIdSelected, open]);

  useEffect(() => {
    if (editMode === "edit") {
      setUnidad(editData.unidad);
      setCantidad(editData.cantidad);
      setInsumoSelected(editData.insumo);
      setTallas(editData.details);
      setCheckTallas(!!editData.details);
    } else {
      setUnidad(null);
      setCantidad(null);
      setInsumoSelected(null);
      setTallas(null);
      setCheckTallas(false);
    }
  }, [editMode, editData, open]);

  const submitFnc = async () => {
    if (!insumoSelected || !unidad) return;
    const data = {
      cantidad: cantidad || 0,
      prendaId: prendaSelected.id || 0,
      insumo: { id: insumoSelected.id, nombre: insumoSelected.nombre, codigo: insumoSelected.codigo },
      unidad,
      details: checkTallas ? tallas : null,
    };
    if (mode === "PUT") {
      if (editMode === "create") {
        await createPrendaInsumo(data);
      } else {
        data.id = editData.id;
        await modifyPrendaInsumo(data);
      }
    } else {
      setDataPI(prev => [...prev, data]);
      setPrendaInsumo(prev => [...prev, data]);
    }
    setUnidad(null);
    setInsumoSelected(null);
    handleClose();
  };

  const handleInsumoChange = (_, value) => {
    setUnidad(value?.details?.presentacion || "");
    setInsumoSelected(value);
  };

  const handleUnidadChange = (_, value, item) => {
    setTallas(prev => prev.map(t => t.talla.id === item.talla.id ? { ...t, unidad: value } : t));
  };

  const changeCantidad = (e, item) => {
    const val = e.target.value;
    setTallas(prev => prev.map(t => t.talla.id === item.talla.id ? { ...t, cantidad: val } : t));
  };

  return (
    <Dialog open={open} maxWidth="lg" onClose={handleClose} fullWidth>
      {/* Header */}
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{
          background: "linear-gradient(135deg, #f43f5e 0%, #ec4899 100%)",
          px: 3, py: 2,
          display: "flex", alignItems: "center", gap: 1.5,
        }}>
          <ContentCutIcon sx={{ color: "#fff", fontSize: 22 }} />
          <Box>
            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, color: "#fff", fontSize: "1rem" }}>
              {editMode === "edit" ? "Editar insumo de prenda" : "Agregar insumo a prenda"}
            </Typography>
            {insumoSelected && (
              <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.75rem", color: "rgba(255,255,255,0.8)" }}>
                {insumoSelected.nombre}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{
          pt: 3, pb: 1,
          background: "var(--modal-bg, #fff)",
          ".dark &": { background: "#1e293b" },
        }}>
        {/* Datos principales */}
        <Box sx={{
            background: "#fff0f6",
            ".dark &": { background: "rgba(244,63,94,0.08)", border: "1px solid rgba(244,63,94,0.2)" },
            border: "1px solid #fce7f3", borderRadius: 2, p: 2.5, mb: 2.5,
          }}>
          <Typography sx={sectionLabelSx}>Datos del insumo</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                getOptionLabel={(insumo) => `${insumo.nombre}${insumo.codigo ? ` — ${insumo.codigo}` : ""}`}
                onChange={handleInsumoChange}
                disablePortal
                options={insumos || []}
                value={insumoSelected}
                sx={inputSx}
                renderInput={(params) => <TextField {...params} label="Insumo" size="small" />}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box>
                      <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.85rem", fontWeight: 600 }}>
                        {option.nombre}
                      </Typography>
                      {option.codigo && (
                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.7rem", color: "#9ca3af" }}>
                          {option.codigo}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                value={unidad || ""}
                label="Presentación / Unidad"
                InputProps={{ readOnly: true }}
                sx={inputSx}
              />
            </Grid>
            {!checkTallas && (
              <Grid item xs={12} md={4}>
                <TextField
                  value={cantidad || ""}
                  onChange={(e) => setCantidad(e.target.value)}
                  fullWidth
                  size="small"
                  type="number"
                  label="Cantidad general"
                  sx={inputSx}
                  InputProps={{
                    startAdornment: <StraightenIcon sx={{ color: "#f472b6", fontSize: 18, mr: 0.5 }} />,
                  }}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checkTallas}
                    onChange={(e) => setCheckTallas(e.target.checked)}
                    sx={{ color: "#f472b6", "&.Mui-checked": { color: "#f43f5e" } }}
                  />
                }
                label={
                  <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.85rem", fontWeight: 600, color: "#374151" }}>
                    Especificar cantidad por talla
                  </Typography>
                }
              />
            </Grid>
          </Grid>
        </Box>

        {/* Tabla de tallas */}
        {checkTallas && tallas && tallas.length > 0 && (
          <Box>
            <Typography sx={sectionLabelSx}>Cantidad por talla</Typography>
            {/* Encabezado tabla */}
            <Box sx={{
              display: "grid", gridTemplateColumns: "250px 1fr 1fr",
              background: "linear-gradient(135deg, #f43f5e 0%, #ec4899 100%)",
              borderRadius: "8px 8px 0 0", px: 2, py: 1,
            }}>
              {["Talla", "Cantidad requerida", "Unidad"].map((h) => (
                <Typography key={h} sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, color: "#fff", fontSize: "0.75rem" }}>
                  {h}
                </Typography>
              ))}
            </Box>
            {/* Filas */}
            <Box sx={{ border: "1px solid #fce7f3", borderTop: "none", borderRadius: "0 0 8px 8px", overflow: "hidden" }}>
              {tallas.map((item, index) => (
                <Box key={item.talla.id} sx={{
                  display: "grid", gridTemplateColumns: "250px 1fr 1fr",
                  px: 2, py: 1.2,
                  background: index % 2 === 0 ? "#fff" : "#fff0f6",
                  ".dark &": { background: index % 2 === 0 ? "#1e293b" : "rgba(244,63,94,0.07)" },
                  borderBottom: index < tallas.length - 1 ? "1px solid #fce7f3" : "none",
                  gap: 1,
                }}>
                  <Chip
                    label={item.talla.nombre}
                    size="small"
                    sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, background: "#fce7f3", color: "#be185d", width: "fit-content" }}
                  />
                  <TextField
                    size="small"
                    label="Cantidad"
                    onChange={(e) => changeCantidad(e, item)}
                    defaultValue={item.cantidad}
                    type="number"
                    sx={inputSx}
                    fullWidth
                  />
                  <Autocomplete
                    size="small"
                    getOptionLabel={(u) => u.name}
                    onChange={(e, value) => handleUnidadChange(e, value, item)}
                    options={UNIDADES}
                    value={item.unidad || DEFAULT_UNIDAD}
                    isOptionEqualToValue={(o, v) => o.name === v?.name}
                    sx={inputSx}
                    renderInput={(params) => <TextField {...params} label="Unidad" />}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {checkTallas && (!tallas || tallas.length === 0) && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.85rem", color: "#9ca3af" }}>
              No hay tallas configuradas para este producto
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{
          px: 3, pb: 2.5, gap: 1,
          background: "var(--modal-bg, #fff)",
          ".dark &": { background: "#1e293b" },
        }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            fontFamily: "'Poppins', sans-serif", fontWeight: 600, borderRadius: 2,
            borderColor: "#f43f5e", color: "#f43f5e",
            "&:hover": { borderColor: "#e11d48", background: "#fff0f6" },
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={submitFnc}
          variant="contained"
          sx={{
            fontFamily: "'Poppins', sans-serif", fontWeight: 600, borderRadius: 2,
            background: "linear-gradient(135deg, #f43f5e 0%, #ec4899 100%)",
            boxShadow: "0 4px 14px rgba(244,63,94,0.35)",
            "&:hover": { background: "linear-gradient(135deg, #e11d48 0%, #db2777 100%)" },
          }}
        >
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InsumosModal;
